import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';

interface POState {
    myPOs: any[];
    pendingApprovals: any[];
    currentPO: any | null;
    isLoading: boolean;
    error: string | null;
}

const initialState: POState = {
    myPOs: [],
    pendingApprovals: [],
    currentPO: null,
    isLoading: false,
    error: null,
};

export const fetchMyPOs = createAsyncThunk('po/fetchMyPOs', async (_, { rejectWithValue }) => {
    try {
        const response = await apiService.getMyPOs();
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message);
    }
});

export const fetchPendingApprovalsPO = createAsyncThunk(
    'po/fetchPendingApprovals',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getPendingApprovalsPO();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const fetchPOById = createAsyncThunk(
    'po/fetchById',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await apiService.getPO(id);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const createPO = createAsyncThunk(
    'po/create',
    async (poData: any, { rejectWithValue }) => {
        try {
            const response = await apiService.createPO(poData);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const createPODraft = createAsyncThunk(
    'po/createDraft',
    async (poData: any, { rejectWithValue }) => {
        try {
            const response = await apiService.createPODraft(poData);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

const poSlice = createSlice({
    name: 'po',
    initialState,
    reducers: {
        clearCurrentPO: (state) => {
            state.currentPO = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch My POs
        builder.addCase(fetchMyPOs.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchMyPOs.fulfilled, (state, action) => {
            state.isLoading = false;
            state.myPOs = action.payload;
        });
        builder.addCase(fetchMyPOs.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Fetch Pending Approvals
        builder.addCase(fetchPendingApprovalsPO.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchPendingApprovalsPO.fulfilled, (state, action) => {
            state.isLoading = false;
            state.pendingApprovals = action.payload;
        });
        builder.addCase(fetchPendingApprovalsPO.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Fetch PO by ID
        builder.addCase(fetchPOById.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchPOById.fulfilled, (state, action) => {
            state.isLoading = false;
            state.currentPO = action.payload;
        });
        builder.addCase(fetchPOById.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Create PO
        builder.addCase(createPO.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(createPO.fulfilled, (state, action) => {
            state.isLoading = false;
            state.currentPO = action.payload;
        });
        builder.addCase(createPO.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Create PO Draft
        builder.addCase(createPODraft.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(createPODraft.fulfilled, (state, action) => {
            state.isLoading = false;
            state.currentPO = action.payload;
        });
        builder.addCase(createPODraft.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });
    },
});

export const { clearCurrentPO, clearError } = poSlice.actions;
export default poSlice.reducer;
